import React, { useEffect, useState } from 'react';
import { Link, useTranslate, useGetIdentity } from 'react-admin';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import moment from 'moment';
import { useTheme } from '@mui/material/styles';

import TimeEntryBarChart from '../time_entries/charts/TimeEntryBarChart';

const MAX_HOURS_DAY = 8;
const PIE_HEIGHT = 120;

const getColor = (value, theme) => {
    if (value >= 90) {
        return theme.palette.mode === 'dark' ? theme.palette.success.main : theme.palette.success.light;
    }
    else if (value >= 80) {
        return theme.palette.mode === 'dark' ? theme.palette.info.main : theme.palette.info.light;
    }
    else if (value >= 60) {
        return theme.palette.mode === 'dark' ? theme.palette.warning.main : theme.palette.warning.light;
    }

    return theme.palette.mode === 'dark' ? theme.palette.error.main : theme.palette.error.light;
};

const TimeEntryBarChartCard = () => {
    const [now] = useState(moment());
    const [data, setData] = useState();
    const [total, setTotal] = useState();
    const [rate, setRate] = useState();
    const [maxHours, setMaxHours] = useState();
    const { data: account } = useGetIdentity();
    const translate = useTranslate();
    const theme = useTheme();

    const nowDate = now.format('YYYY-MM-DD');
    const lastWeekDate = moment(now).subtract(6, 'days').format('YYYY-MM-DD');

    useEffect(() => {
        let dates = [];
        const workingDays = data?.reduce((acc, item) => {
            const date = new Date(item.date);
            if (!dates.includes(item.date) && ![0, 6].includes(date.getDay())) {
                dates.push(item.date);
                return acc += 1;
            }
            dates.push(item.date);
            return acc;
        }, 0);

        const maxHours = workingDays * MAX_HOURS_DAY;
        setMaxHours(maxHours);
        setRate((total / maxHours) * 100);
    }, [data, total]);

    if (!account?.id) {
        return null;
    }

    return (
        <Card sx={{ height: '100%', padding: '12px' }}>
            <Stack direction="row" justifyContent="space-between" spacing={1}>
                <Link to={`/time_entries/stats?filter={"user_id":${account?.id},"group_by":"project","freq":"daily"}`} sx={{ textDecoration: 'none' }}>
                    <Typography variant="h6">{translate('Last 7 days timesheet')}</Typography>
                </Link>
                <Link to="/time_entries/calendar" sx={{ textDecoration: 'none' }}>
                    <Button variant="text" size="small" sx={{ minWidth: 'auto' }}><CalendarMonthIcon /></Button>
                </Link>
            </Stack>
            <Box sx={{ width: '100%', height: '160px', marginTop: '15px' }}>
                <TimeEntryBarChart
                    startDate={lastWeekDate}
                    endDate={nowDate}
                    userId={account?.id}
                    groupBy="project"
                    showLegend={false}
                    showTotal={false}
                    freq="daily"
                    referenceLineLabel=" "
                    setData={setData}
                    setTotal={setTotal}
                    margin={{ left: -40 }}
                />
            </Box>
            <Box>
                <Grid container spacing={2} sx={{ marginTop: '10px' }}>
                    <Grid size={{ lg: 7, md: 12, sm: 12, xs: 12 }}>
                        <Box marginBottom={`-${PIE_HEIGHT / 2}px`}>
                            <ResponsiveContainer width="100%" height={PIE_HEIGHT}>
                                <PieChart>
                                    <Pie
                                        data={[{ value: rate < 100 ? rate : 100 }, { value: rate < 100 ? 100 - rate : 0 }]}
                                        startAngle={180}
                                        endAngle={0}
                                        innerRadius="70%"
                                        outerRadius="100%"
                                        fill="#8884d8"
                                        dataKey="value"
                                        stroke={null}
                                    >
                                        <Cell fill={getColor(rate, theme)} />
                                        <Cell fill={rate === 0 ? theme.palette.error.main : theme.palette.divider} />
                                    </Pie>
                                </PieChart>
                            </ResponsiveContainer>
                        </Box>
                    </Grid>
                    <Grid size={{ lg: 5, md: 12, sm: 12, xs: 12 }}>
                        <Box sx={{ margin: '15px 0' }}>
                            <Typography>{translate('Rate')}: {rate ? rate?.toFixed(2) : 0}%</Typography>
                            <Typography>{translate('Total')}: {total}{maxHours ? `/${maxHours}` : ''}h</Typography>
                        </Box>
                    </Grid>
                </Grid>
            </Box>
        </Card>
    );
};

export default TimeEntryBarChartCard;